import { IconButton, Play, Pause, Refresh as RefreshIcon, Volume2, IconButtonSize, IconSize, ButtonType, CloseSmall } from '@soteria/design-system-ve';

import * as styles from './InlineAudioPlayer.css';
import { useAudioPlayerControls } from './useAudioPlayerControls';

interface InlineAudioPlayerProps {
  audioId: string;
  audioUrl: string;
  transcription?: string;
  mediaServiceUrl?: string;
}

export function InlineAudioPlayer({ audioId, audioUrl, mediaServiceUrl }: InlineAudioPlayerProps) {
  // Get context and state from custom hook
  const { playerState, handlers, refs, isActive, progressPercentage, formatTime } = useAudioPlayerControls(audioId, audioUrl, mediaServiceUrl);

  const { status, error, currentTime, duration, volume, isExpanded, isMuted } = playerState;

  const { handlePlayClick, handleRetry, handleProgressBarClick, handleVolumeChange, toggleMute, seekTo, toggleExpand, pauseAudio } = handlers;

  const { progressBarRef } = refs;

  // Render the default play button (not expanded) - matches ContentCard styling
  const renderDefaultPlayButton = () => (
    <IconButton
      aria-label="Play Audio"
      buttonSize={IconButtonSize.Small}
      buttonType={ButtonType.Brand}
      className={styles.playButton}
      icon={Play}
      iconSize={IconSize.XXSmall}
      title="Play Audio"
      onClick={handlePlayClick}
    />
  );

  // Render loading button
  const renderLoadingButton = () => (
    <IconButton
      disabled
      aria-label="Loading Audio"
      buttonSize={IconButtonSize.Small}
      buttonType={ButtonType.Brand}
      className={isExpanded ? styles.loadingButton : `${styles.playButton} ${styles.loadingButton}`}
      icon={Play}
      iconSize={IconSize.XXSmall}
      title="Loading Audio"
    />
  );

  // Render error button
  const renderErrorButton = () => (
    <IconButton
      aria-label="Retry Loading Audio"
      buttonSize={IconButtonSize.Small}
      buttonType={isExpanded ? ButtonType.Tertiary : ButtonType.Brand}
      className={isExpanded ? '' : styles.playButton}
      icon={RefreshIcon}
      iconSize={IconSize.XXSmall}
      title="Retry Loading Audio"
      onClick={handleRetry}
    />
  );

  // Render expanded play/pause button
  const renderExpandedPlayButton = () => (
    <IconButton
      aria-label={status === 'playing' ? 'Pause Audio' : 'Play Audio'}
      buttonSize={IconButtonSize.Small}
      buttonType={ButtonType.Brand}
      icon={status === 'playing' ? Pause : Play}
      iconSize={IconSize.XXSmall}
      title={status === 'playing' ? 'Pause Audio' : 'Play Audio'}
      onClick={handlePlayClick}
    />
  );

  // Determine which button to show based on status
  const renderActionButton = () => {
    // Always show play button when not expanded, regardless of status
    if (!isActive || status === 'idle' || !isExpanded) {
      return renderDefaultPlayButton();
    }

    if (status === 'loading') {
      return renderLoadingButton();
    }

    if (status === 'error') {
      return renderErrorButton();
    }

    return renderExpandedPlayButton();
  };

  // Render error state for the expanded player
  const renderErrorState = () => (
    <div className={styles.expandedPlayer}>
      <div className={styles.errorAlert}>{error || 'Failed to load audio'}</div>
      <IconButton
        aria-label="Retry"
        buttonSize={IconButtonSize.Small}
        buttonType={ButtonType.Brand}
        icon={RefreshIcon}
        iconSize={IconSize.XXSmall}
        title="Retry"
        onClick={handleRetry}
      />
    </div>
  );

  // Render the progress bar component
  const renderProgressControls = () => (
    <div style={{ display: 'flex', flex: 1, alignItems: 'center', overflow: 'hidden' }}>
      {/* Progress bar */}
      <div
        ref={progressBarRef}
        aria-label="Audio progress"
        aria-valuemax={duration}
        aria-valuemin={0}
        aria-valuenow={currentTime}
        className={styles.progressBarContainer}
        role="slider"
        tabIndex={0}
        onClick={handleProgressBarClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (progressBarRef.current) {
              // No need to get rect for keyboard navigation, just use a fixed position
              const centerPosition = 0.5; // Move to middle of bar on keyboard activation
              const newTime = centerPosition * duration;

              seekTo(newTime);
            }
          }
        }}
      >
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPercentage}%` }} />
        </div>
      </div>

      {/* Time display */}
      <div className={styles.timeDisplay}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
    </div>
  );

  // Render volume controls
  const renderVolumeControls = () => (
    <div className={styles.volumeControls}>
      <IconButton
        aria-label={isMuted ? 'Unmute' : 'Mute'}
        buttonSize={IconButtonSize.Small}
        buttonType={ButtonType.Brand}
        icon={Volume2}
        iconSize={IconSize.XXSmall}
        title={isMuted ? 'Unmute' : 'Mute'}
        onClick={toggleMute}
      />
      <div className={styles.volumeSliderContainer}>
        <input className={styles.volumeSlider} max="1" min="0" step="0.01" type="range" value={volume} onChange={handleVolumeChange} />
      </div>
    </div>
  );

  // Render close button
  const renderCloseButton = () => (
    <IconButton
      aria-label="Close Audio Player"
      buttonSize={IconButtonSize.Small}
      buttonType={ButtonType.Brand}
      icon={CloseSmall}
      iconSize={IconSize.XXSmall}
      title="Close Audio Player"
      onClick={e => {
        e.stopPropagation();
        // Pause audio if it's currently playing
        if (status === 'playing') {
          pauseAudio();
        }

        toggleExpand();
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          // Pause audio if it's currently playing
          if (status === 'playing') {
            pauseAudio();
          }

          toggleExpand();
        }
      }}
    />
  );

  // Render the expanded audio player
  const renderExpandedPlayer = () => {
    if (!isActive || !isExpanded) return null;

    if (status === 'error') {
      return renderErrorState();
    }

    return (
      <div aria-label="Audio player controls" className={styles.expandedPlayer} role="group">
        {renderProgressControls()}
        {renderVolumeControls()}
        {renderCloseButton()}
      </div>
    );
  };

  return (
    <div className={styles.playerContainer}>
      <div
        className={`
          ${styles.inlineAudioPlayer}
          ${isActive && isExpanded ? styles.expanded : ''}
        `}
      >
        {/* Play button is always visible */}
        {renderActionButton()}

        {/* Expanded content appears when active and expanded */}
        {isActive && isExpanded && renderExpandedPlayer()}
      </div>
    </div>
  );
}
